import { useMemo, useState } from "react";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { TaskType } from "@common/types/task";
import { PersistableView } from "@common/types/views/views";
import { useResolveTaskViewQuery } from "@api/closeTemplate.api";
import { hasTaskDataPreview } from "@components/drawers/taskCreationPreviewRegistry";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { DateOnlyPeriod } from "../closePeriodSelector.utils";
import { readReviewFieldsFromViewConfig } from "../closeTemplateTaskViewConfig";

const RESOLVE_DEBOUNCE_MS = 400;

interface UseResolvedTaskViewParams {
    taskType: TaskType;
    viewConfig: CloseTemplateViewConfig | null;
    entityId: number | null;
    isEntityLoading: boolean;
    noEntityReason: string;
    period: DateOnlyPeriod;
}

export interface ResolvedTaskView {
    /**
     * The row filters the task would use for the entity, with the template's
     * abstract filters translated. Null when no row can match, or the view
     * can't be resolved yet; `skipReason` then says why.
     */
    apiFilters: PersistableView["filters"] | null;
    skipReason: string | null;
    isLoading: boolean;
}

const NO_REVIEW_FIELDS_REASON =
    "Select at least one field to review to preview this task.";

const DISPLAY_FIELDS = new Set(["columns", "options"]);

const isMissingReviewFields = ({
    taskType,
    viewConfig,
}: {
    taskType: TaskType;
    viewConfig: CloseTemplateViewConfig | null;
}): boolean =>
    taskType === TaskType.TRANSACTION_REVIEW &&
    readReviewFieldsFromViewConfig(viewConfig).length === 0;

/**
 * Column and display settings never change which rows match, so the same
 * filters resolve to the same view however the columns are arranged.
 */
const getResolvedFiltersKey = (
    viewConfig: CloseTemplateViewConfig | null,
): string => {
    if (!viewConfig) {
        return "";
    }
    const filterEntries = Object.entries(viewConfig).filter(
        ([field]) => !DISPLAY_FIELDS.has(field),
    );
    return JSON.stringify(filterEntries);
};

const useFilterStableViewConfig = (
    viewConfig: CloseTemplateViewConfig | null,
): CloseTemplateViewConfig | null => {
    const key = getResolvedFiltersKey(viewConfig);
    const [stable, setStable] = useState({ key, viewConfig });
    if (stable.key !== key) {
        setStable({ key, viewConfig });
    }
    return stable.viewConfig;
};

export const useResolvedTaskView = ({
    taskType,
    viewConfig,
    entityId,
    isEntityLoading,
    noEntityReason,
    period,
}: UseResolvedTaskViewParams): ResolvedTaskView => {
    const stableViewConfig = useFilterStableViewConfig(viewConfig);
    const request = {
        taskType,
        viewConfig: stableViewConfig,
        entityId,
        period,
    };
    const debouncedRequest = useDebouncedValue(request, RESOLVE_DEBOUNCE_MS);
    const hasSettled =
        JSON.stringify(debouncedRequest) === JSON.stringify(request);
    const body = useMemo(
        () =>
            hasTaskDataPreview(debouncedRequest.taskType) &&
            debouncedRequest.entityId !== null &&
            !isMissingReviewFields(debouncedRequest)
                ? {
                      taskType: debouncedRequest.taskType,
                      viewConfig: debouncedRequest.viewConfig,
                      entityId: debouncedRequest.entityId,
                      ...debouncedRequest.period,
                  }
                : null,
        [debouncedRequest],
    );
    const query = useResolveTaskViewQuery(body);

    if (isEntityLoading) {
        return { apiFilters: null, skipReason: null, isLoading: true };
    }
    if (entityId === null) {
        return {
            apiFilters: null,
            skipReason: noEntityReason,
            isLoading: false,
        };
    }
    if (isMissingReviewFields(request)) {
        return {
            apiFilters: null,
            skipReason: NO_REVIEW_FIELDS_REASON,
            isLoading: false,
        };
    }
    if (!hasSettled || query.isLoading) {
        return { apiFilters: null, skipReason: null, isLoading: true };
    }
    if (query.isError) {
        return {
            apiFilters: null,
            skipReason: query.error.message,
            isLoading: false,
        };
    }
    const resolvedView = query.data?.views[0];
    return {
        apiFilters: resolvedView?.filters ?? null,
        skipReason: query.data?.skipReason ?? null,
        isLoading: false,
    };
};
